import { ActionIcon, Box, Button, Group, Paper, SegmentedControl, Stack, Switch, Text, Title, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import MapboxDraw, { DrawCreateEvent, DrawUpdateEvent } from '@mapbox/mapbox-gl-draw';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconFocus2 } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Map, MapMouseEvent } from 'mapbox-gl';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, MapRef, NavigationControl, Source } from 'react-map-gl';

import { useAssignmentQueries } from '../../data-access/queries/gp-sampling-assignment.queries';
import { useSamplingQueries } from '../../data-access/queries/sampling';
import {
  PreviewPolygonDto,
  PreviewPolygonResponse,
  SamplingPeriod,
} from '../../data-access/types/gp-sampling-assignment';
import {
  AssignmentMapConfig,
  GP_ASSIGNMENT_CONFIG,
  isTeamAssignmentVariant,
} from '../../lib/assignment-context';
import { NgpSamplingRecapModal } from '../ngp-sampling-recap/modal';

import {
  buildColorModeSegments,
  type AssignmentMapColorMode,
  type DrawActionMode,
} from './assignment-map-constants';
import {
  assignmentFiltersToApiBody,
  buildFilterOptionsFromPoints,
  EMPTY_ASSIGNMENT_MAP_FILTERS,
  filterAssignmentPoints,
} from './assignment-map-filters';
import { AssignmentMapSidebar } from './assignment-map-sidebar';
import { AssignmentMapLegendPanel } from './assignment-map-legend-panel';
import { AssignmentPointPopup, AssignmentPointPopupData } from './assignment-point-popup';
import {
  ASSIGNMENT_POINTS_LAYER_ID,
  ASSIGNMENT_POINTS_SOURCE_ID,
  buildAssignmentPointsGeoJson,
} from './assignment-points-layer';
import { DepartmentShareOverlay } from './department-share-overlay';
import { zoomMapToPoints } from './map-zoom';
import { useAssignmentPointClick } from './use-assignment-point-click';

import { DrawControl, PfMap } from '@map';
import { FullMainHeight } from '@ui';

function previewPurpose(
  mode: DrawActionMode,
  clearAssignment: boolean,
): PreviewPolygonDto['purpose'] {
  if (mode === 'sms') return 'sms';
  if (mode === 'make_visible') return 'make_visible';
  if (mode === 'reassign_sampler') return 'reassign';
  if (mode === 'confirm_laboratory') return 'confirm';
  if (mode === 'assign_laboratory') return 'assign_laboratory';
  if (mode === 'sampler' && clearAssignment) return 'unassign_sampler';
  return 'sampler';
}

export interface SamplingAssignmentMapPageProps {
  config?: AssignmentMapConfig;
  referenceDate?: string;
  headerExtra?: ReactNode;
}

export const SamplingAssignmentMapPage: FC<SamplingAssignmentMapPageProps> = ({
  config = GP_ASSIGNMENT_CONFIG,
  referenceDate,
  headerExtra,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const assignmentQueries = useAssignmentQueries(config);
  const assignmentApi = assignmentQueries.api;
  const samplingQueries = useSamplingQueries();
  const drawRef = useRef<MapboxDraw | null>(null);
  const mapRef = useRef<MapRef>(null);

  const isNgp = config.clause === 'new';
  const capabilities = config.capabilities;
  const isTeamAssignment = isTeamAssignmentVariant(config);
  const defaultMode: DrawActionMode = capabilities.drawModes[0] ?? 'sampler';

  const [mode, setMode] = useState<DrawActionMode>(defaultMode);
  const [period, setPeriod] = useState<SamplingPeriod>('spring');
  const [preview, setPreview] = useState<PreviewPolygonResponse | null>(null);
  const [selectedSmsDispatchId, setSelectedSmsDispatchId] = useState<number | null>(null);
  const [samplerOrganizationId, setSamplerOrganizationId] = useState<string | null>(null);
  const [samplerUserId, setSamplerUserId] = useState<string | null>(null);
  const [reassignSamplerId, setReassignSamplerId] = useState<string | null>(null);
  const [visibleToSampler, setVisibleToSampler] = useState(false);
  const [laboratoryId, setLaboratoryId] = useState<string | null>(null);
  const [clearAssignment, setClearAssignment] = useState(false);
  const [popupData, setPopupData] = useState<AssignmentPointPopupData | null>(null);
  const [mapFilters, setMapFilters] = useState(EMPTY_ASSIGNMENT_MAP_FILTERS);
  const [colorMode, setColorMode] = useState<AssignmentMapColorMode>(
    capabilities.colorModes[0] ?? 'sampler',
  );
  const [legendPeriod, setLegendPeriod] = useState<SamplingPeriod>('spring');
  const [showDepartmentShares, setShowDepartmentShares] = useState(true);
  const [recapOpened, setRecapOpened] = useState(false);

  const selectedSamplerOrganizationId = samplerOrganizationId
    ? Number(samplerOrganizationId)
    : undefined;

  const i18n = (key: string, options?: Record<string, unknown>) =>
    t(`${config.i18nPrefix}.${key}`, options);

  const mapQuery = useQuery(assignmentQueries.map(referenceDate));
  const organisationsQuery = useQuery({
    ...assignmentQueries.organisations(),
    enabled: capabilities.filterOsOrganizations,
  });
  const samplerOrganizationsQuery = useQuery(assignmentQueries.samplerOrganizations());
  const allSamplersQuery = useQuery({
    ...assignmentQueries.samplers(),
    enabled: isNgp,
  });
  const actionSamplersQuery = useQuery({
    ...assignmentQueries.samplers(selectedSamplerOrganizationId),
    enabled:
      isNgp &&
      capabilities.showSamplerOrganizationSelect &&
      selectedSamplerOrganizationId != null,
  });
  const smsProgressQuery = useQuery({
    ...assignmentQueries.smsProgress(selectedSmsDispatchId ?? 0),
    enabled: selectedSmsDispatchId != null && selectedSmsDispatchId > 0,
    refetchInterval: (query) => {
      const farmers = query.state.data?.farmers ?? [];
      const waiting = farmers.some(
        (farmer) =>
          farmer.validation_form_status == null ||
          farmer.validation_form_status === 'INCOMPLETE',
      );
      return waiting ? 3000 : false;
    },
  });
  const orderValuesQuery = useQuery({
    ...samplingQueries.orders.values({}),
    enabled: !isNgp,
  });
  const laboratoriesQuery = useQuery(assignmentQueries.laboratories());

  const withFilters = useCallback(
    <T,>(dto: T): T =>
      ({
        ...dto,
        ...assignmentFiltersToApiBody(mapFilters, referenceDate),
      }) as T,
    [mapFilters, referenceDate],
  );

  const resetDrawState = () => {
    setPreview(null);
    drawRef.current?.deleteAll();
  };

  const handleModeChange = (nextMode: DrawActionMode) => {
    setMode(nextMode);
    setClearAssignment(false);
    resetDrawState();
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: assignmentQueries.all() });
    if (selectedSmsDispatchId) {
      queryClient.invalidateQueries({
        queryKey: assignmentQueries.smsProgress(selectedSmsDispatchId).queryKey,
      });
    }
  };

  const showMutationError = (error: Error) => {
    notifications.show({
      message: error.message || t('common.error.pageError'),
      color: 'red',
    });
  };

  const onActionSuccess = (message: string) => {
    notifications.show({ message, color: 'green' });
    resetDrawState();
    invalidate();
  };

  const forceOrganicMutation = useMutation({
    mutationFn: ({ pointId, forced }: { pointId: number; forced: boolean }) =>
      assignmentApi.actions.forceOrganicOk({ point_id: pointId, forced }),
    onSuccess: (data) => {
      notifications.show({
        message: i18n(data.organic_forced_ok ? 'forceOrganicOkDone' : 'clearOrganicForceDone'),
        color: 'green',
      });
      setPopupData((current) => {
        if (!current || current.point.id !== data.point_id) {
          return current;
        }
        return {
          ...current,
          point: {
            ...current.point,
            organic_status: data.organic_status,
            organic_forced_ok: data.organic_forced_ok,
          },
        };
      });
      invalidate();
    },
    onError: showMutationError,
  });

  const handleSetOrganicForced = (pointId: number, forced: boolean) => {
    modals.openConfirmModal({
      title: i18n(forced ? 'forceOrganicOkTitle' : 'clearOrganicForceTitle'),
      children: (
        <Text size="sm">
          {i18n(forced ? 'forceOrganicOkConfirm' : 'clearOrganicForceConfirm')}
        </Text>
      ),
      labels: {
        confirm: i18n(forced ? 'forceOrganicOk' : 'clearOrganicForce'),
        cancel: t('common.cancel'),
      },
      confirmProps: forced ? { color: 'orange' } : undefined,
      onConfirm: () => forceOrganicMutation.mutate({ pointId, forced }),
    });
  };

  const assignSamplerMutation = useMutation({
    mutationFn: assignmentApi.actions.assignSampler,
    onSuccess: (_data, variables) => {
      onActionSuccess(variables.clear ? i18n('samplerCleared') : i18n('samplerAssigned'));
      if (variables.clear) {
        setClearAssignment(false);
      }
    },
    onError: showMutationError,
  });

  const sendSmsMutation = useMutation({
    mutationFn: assignmentApi.actions.sendSms,
    onSuccess: (data) => {
      onActionSuccess(i18n('smsQueued'));
      if (data.dispatch_id) {
        setSelectedSmsDispatchId(data.dispatch_id);
      }
    },
    onError: showMutationError,
  });

  const makeVisibleMutation = useMutation({
    mutationFn: (dto: Record<string, unknown>) =>
      assignmentApi.actions.makeVisible(dto),
    onSuccess: (data) =>
      onActionSuccess(i18n('pointsMadeVisible', { count: data.updated_count ?? 0 })),
    onError: showMutationError,
  });

  const reassignMutation = useMutation({
    mutationFn: assignmentApi.actions.reassignSampler,
    onSuccess: () => {
      onActionSuccess(i18n('samplerReassigned'));
      setReassignSamplerId(null);
    },
    onError: showMutationError,
  });

  const assignLaboratoryMutation = useMutation({
    mutationFn: assignmentApi.actions.assignLaboratory,
    onSuccess: (_data, variables) => {
      onActionSuccess(
        variables.laboratory == null ? i18n('laboratoryCleared') : i18n('laboratoryAssigned'),
      );
      setLaboratoryId(null);
      setClearAssignment(false);
    },
    onError: showMutationError,
  });

  const confirmMutation = useMutation({
    mutationFn: assignmentApi.actions.confirmLaboratory,
    onSuccess: () => {
      onActionSuccess(i18n('assignmentConfirmed'));
      setLaboratoryId(null);
    },
    onError: showMutationError,
  });

  const previewMutation = useMutation({
    mutationFn: assignmentApi.map.preview,
    onSuccess: (data) => setPreview(data),
    onError: showMutationError,
  });

  const requestPreview = useCallback(
    (polygon: GeoJSON.Polygon) => {
      previewMutation.mutate(
        withFilters({
          polygon,
          purpose: previewPurpose(mode, clearAssignment),
          period,
        }),
      );
    },
    [clearAssignment, mode, period, previewMutation, withFilters],
  );

  const requestPreviewDebounced = useDebouncedCallback(requestPreview, 300);

  const previewFromDrawEvent = useCallback(
    (event: DrawCreateEvent | DrawUpdateEvent) => {
      setPopupData(null);
      const feature = event.features[0];
      if (feature?.geometry.type !== 'Polygon') return;
      return feature.geometry as GeoJSON.Polygon;
    },
    [],
  );

  const handleDrawCreate = useCallback(
    (event: DrawCreateEvent) => {
      const polygon = previewFromDrawEvent(event);
      if (polygon) requestPreview(polygon);
    },
    [previewFromDrawEvent, requestPreview],
  );

  const handleDrawUpdate = useCallback(
    (event: DrawUpdateEvent) => {
      const polygon = previewFromDrawEvent(event);
      if (polygon) requestPreviewDebounced(polygon);
    },
    [previewFromDrawEvent, requestPreviewDebounced],
  );

  const handleDrawDelete = useCallback(() => {
    setPreview(null);
    setPopupData(null);
  }, []);

  const periodHighlightMode = mode === 'sms' ? 'sms' : 'sampler';
  const mapPoints = mapQuery.data?.points ?? [];
  const filteredMapPoints = useMemo(
    () => filterAssignmentPoints(mapPoints, mapFilters),
    [mapFilters, mapPoints],
  );

  const pointsGeoJson = useMemo(
    () =>
      buildAssignmentPointsGeoJson(filteredMapPoints, {
        isNgp,
        colorMode,
        legendPeriod,
        periodHighlightMode,
        samplerLegendMode: capabilities.samplerLegendMode,
      }),
    [capabilities.samplerLegendMode, colorMode, filteredMapPoints, isNgp, legendPeriod, periodHighlightMode],
  );

  const handleMapClick = useAssignmentPointClick(filteredMapPoints, (data) => {
    setPopupData(data);
    if (capabilities.smsReadOnly && data?.point.sms_dispatch_id) {
      setSelectedSmsDispatchId(data.point.sms_dispatch_id);
    }
  });

  const handleMapClickWithDrawGuard = useCallback(
    (event: MapMouseEvent) => {
      const drawMode = drawRef.current?.getMode();
      if (drawMode && drawMode !== 'simple_select') {
        return;
      }
      handleMapClick(event);
    },
    [handleMapClick],
  );

  const zoomTargetPoints = useMemo(() => {
    const all = mapQuery.data?.points ?? [];
    if (preview?.point_ids.length) {
      const ids = new Set(preview.point_ids);
      return all.filter((p) => ids.has(p.id));
    }
    if (selectedSmsDispatchId) {
      return all.filter((p) => p.sms_dispatch_id === selectedSmsDispatchId);
    }
    return all;
  }, [mapQuery.data?.points, preview?.point_ids, selectedSmsDispatchId]);

  const zoomToTargetPoints = useCallback(() => {
    zoomMapToPoints(mapRef.current?.getMap(), zoomTargetPoints);
  }, [zoomTargetPoints]);

  const handleMapLoad = useCallback(
    (event: { target: Map }) => {
      const points = mapQuery.data?.points ?? [];
      if (points.length) {
        zoomMapToPoints(event.target, points);
      }
    },
    [mapQuery.data?.points],
  );

  const samplerOptions = useMemo(() => {
    const source = capabilities.showSamplerOrganizationSelect
      ? actionSamplersQuery.data
      : allSamplersQuery.data;

    if (isNgp) {
      return (
        source?.map(({ id, first_name, last_name, organization }) => ({
          value: id.toString(),
          label: `${first_name} ${last_name}${organization?.name ? ` (${organization.name})` : ''}`,
        })) ?? []
      );
    }
    return (
      orderValuesQuery.data?.sampler.map(({ id, first_name, last_name }) => ({
        value: id.toString(),
        label: `${first_name} ${last_name}`,
      })) ?? []
    );
  }, [
    actionSamplersQuery.data,
    allSamplersQuery.data,
    capabilities.showSamplerOrganizationSelect,
    isNgp,
    orderValuesQuery.data?.sampler,
  ]);

  const samplerOrganizationOptions = useMemo(
    () =>
      samplerOrganizationsQuery.data?.map(({ id, name }) => ({
        value: id.toString(),
        label: name ?? `#${id}`,
      })) ?? [],
    [samplerOrganizationsQuery.data],
  );

  const laboratoryOptions = useMemo(
    () =>
      laboratoriesQuery.data?.map(({ id, name }) => ({
        value: id.toString(),
        label: name,
      })) ?? [],
    [laboratoriesQuery.data],
  );

  const filterOptions = useMemo(
    () =>
      buildFilterOptionsFromPoints(
        mapPoints,
        samplerOptions,
        allSamplersQuery.data,
        organisationsQuery.data?.results,
        laboratoryOptions,
      ),
    [
      allSamplersQuery.data,
      laboratoryOptions,
      mapPoints,
      organisationsQuery.data?.results,
      samplerOptions,
    ],
  );

  const getDrawnPolygon = (): GeoJSON.Polygon | null => {
    const draw = drawRef.current;
    if (!draw) return null;
    const feature = draw.getAll().features[0];
    if (!feature || feature.geometry.type !== 'Polygon') return null;
    return feature.geometry as GeoJSON.Polygon;
  };

  useEffect(() => {
    const polygon = getDrawnPolygon();
    if (!polygon) return;
    previewMutation.mutate(
      withFilters({
        polygon,
        purpose: previewPurpose(mode, clearAssignment),
        period,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-preview when filters/context change
  }, [clearAssignment, mapFilters, referenceDate, mode, period]);

  const handlePrimaryAction = () => {
    const polygon = getDrawnPolygon();
    if (!polygon) return;

    switch (mode) {
      case 'sms':
        sendSmsMutation.mutate(withFilters({ polygon, period }));
        break;
      case 'sampler':
        if (clearAssignment) {
          assignSamplerMutation.mutate(withFilters({ polygon, clear: true }));
          break;
        }
        if (!samplerUserId) return;
        assignSamplerMutation.mutate(
          withFilters({
            polygon,
            sampler: Number(samplerUserId),
            visible: visibleToSampler,
            period,
          }),
        );
        break;
      case 'assign_laboratory':
        if (!clearAssignment && !laboratoryId) return;
        assignLaboratoryMutation.mutate(
          withFilters({
            polygon,
            laboratory: clearAssignment ? null : Number(laboratoryId),
          }),
        );
        break;
      case 'make_visible':
        makeVisibleMutation.mutate(
          withFilters({
            polygon,
            ...(samplerUserId ? { sampler_id: Number(samplerUserId) } : {}),
          }),
        );
        break;
      case 'reassign_sampler':
        if (!reassignSamplerId) return;
        reassignMutation.mutate(
          withFilters({
            polygon,
            sampler: Number(reassignSamplerId),
            ...(isTeamAssignment ? {} : { period }),
          }),
        );
        break;
      case 'confirm_laboratory':
        if (!laboratoryId) return;
        confirmMutation.mutate(
          withFilters({
            polygon,
            laboratory: Number(laboratoryId),
            analysis_type: 'carbon',
          }),
        );
        break;
    }
  };

  const primaryActionLabel = () => {
    if (isTeamAssignment) {
      return i18n('teamAssignmentAssign');
    }
    switch (mode) {
      case 'sms':
        return i18n('sendSmsZone');
      case 'sampler':
        return clearAssignment ? i18n('clearSampler') : i18n('assignSampler');
      case 'assign_laboratory':
        return clearAssignment ? i18n('clearLaboratory') : i18n('assignLaboratory');
      case 'make_visible':
        return i18n('actionMakeVisible');
      case 'reassign_sampler':
        return i18n('actionReassign');
      case 'confirm_laboratory':
        return i18n('confirmAssignment');
      default:
        return i18n('sendSmsZone');
    }
  };

  const primaryActionDisabled = () => {
    if (!preview?.count) return true;
    if (mode === 'sampler' && !clearAssignment && !samplerUserId) return true;
    if (mode === 'assign_laboratory' && !clearAssignment && !laboratoryId) return true;
    if (mode === 'reassign_sampler' && !reassignSamplerId) return true;
    if (mode === 'confirm_laboratory' && !laboratoryId) return true;
    return false;
  };

  const primaryActionLoading =
    assignSamplerMutation.isPending ||
    assignLaboratoryMutation.isPending ||
    sendSmsMutation.isPending ||
    makeVisibleMutation.isPending ||
    reassignMutation.isPending ||
    confirmMutation.isPending;

  const periodWindows = mapQuery.data?.period_windows;
  const periodWindowHint = useMemo(() => {
    if (!isNgp) return undefined;
    const window = periodWindows?.[period];
    if (!window) return undefined;
    return i18n(`periodWindow_${period}`, {
      start: window.start,
      end: window.end,
      crops: window.crops.join(' / '),
    });
  }, [i18n, isNgp, period, periodWindows]);

  const drawHint = () => {
    if (isTeamAssignment) {
      return i18n('drawHintTeamAssignment');
    }
    switch (mode) {
      case 'sms':
        return i18n('drawHintSms');
      case 'sampler':
        return i18n('drawHintSampler');
      case 'assign_laboratory':
        return i18n('drawHintLaboratory');
      case 'make_visible':
        return i18n('drawHintMakeVisible');
      case 'reassign_sampler':
        return i18n('drawHintReassign');
      case 'confirm_laboratory':
        return i18n('drawHintConfirm');
      default:
        return i18n('drawHintSms');
    }
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between" wrap="nowrap" w="100%">
            <Stack gap={2}>
              <Title order={2}>
                {isTeamAssignment ? i18n('teamAssignmentTitle') : i18n('title')}
              </Title>
              {isNgp && config.campaignYear ? (
                <Text c="dimmed" size="sm">
                  {i18n('campaignYear', { year: config.campaignYear })}
                </Text>
              ) : null}
            </Stack>
            <Group gap="md" wrap="nowrap">
              {isNgp && !isTeamAssignment ? (
                <Button onClick={() => setRecapOpened(true)} variant="light">
                  {i18n('recap.open')}
                </Button>
              ) : null}
              {capabilities.showReferenceDate ? headerExtra : null}
              {isNgp && !isTeamAssignment ? (
                <Switch
                  checked={showDepartmentShares}
                  label={i18n('departmentOverlay')}
                  onChange={(event) => setShowDepartmentShares(event.currentTarget.checked)}
                />
              ) : null}
              <SegmentedControl
                data={buildColorModeSegments(i18n, { modes: capabilities.colorModes })}
                onChange={(value) => setColorMode(value as AssignmentMapColorMode)}
                value={colorMode}
              />
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.FlexContent>
        <FullMainHeight.Sidebar size="lg">
          <AssignmentMapSidebar
            capabilities={capabilities}
            clearAssignment={clearAssignment}
            defaultMode={defaultMode}
            drawHint={drawHint()}
            filterOptions={filterOptions}
            filteredCount={filteredMapPoints.length}
            filters={mapFilters}
            i18n={i18n}
            isNgp={isNgp}
            laboratoryId={laboratoryId}
            laboratoryOptions={laboratoryOptions}
            mode={mode}
            onClearAssignmentChange={setClearAssignment}
            onFiltersChange={setMapFilters}
            onLaboratoryIdChange={setLaboratoryId}
            onModeChange={handleModeChange}
            onPeriodChange={setPeriod}
            onPrimaryAction={handlePrimaryAction}
            onReassignSamplerIdChange={setReassignSamplerId}
            onSamplerOrganizationIdChange={setSamplerOrganizationId}
            onSamplerUserIdChange={setSamplerUserId}
            onSelectSmsDispatch={setSelectedSmsDispatchId}
            onVisibleToSamplerChange={setVisibleToSampler}
            period={period}
            periodWindowHint={periodWindowHint}
            preview={preview}
            primaryActionDisabled={primaryActionDisabled()}
            primaryActionLabel={primaryActionLabel()}
            primaryActionLoading={primaryActionLoading}
            reassignSamplerId={reassignSamplerId}
            recentActions={mapQuery.data?.recent_actions ?? []}
            samplerOrganizationId={samplerOrganizationId}
            samplerOrganizationOptions={samplerOrganizationOptions}
            samplerOptions={samplerOptions}
            samplerUserId={samplerUserId}
            selectedSmsDispatchId={selectedSmsDispatchId}
            smsProgress={smsProgressQuery.data}
            totalCount={mapPoints.length}
            visibleToSampler={visibleToSampler}
          />
        </FullMainHeight.Sidebar>
        <Box
          flex={1}
          h="100%"
          mih={0}
          style={{ alignSelf: 'stretch', minHeight: 0, minWidth: 0 }}
        >
          <Paper h="100%" p={0} pos="relative" radius="sm" withBorder>
            <Tooltip label={i18n('zoomToFeatures')}>
              <ActionIcon
                aria-label={i18n('zoomToFeatures')}
                disabled={zoomTargetPoints.length === 0}
                onClick={zoomToTargetPoints}
                pos="absolute"
                style={{ left: 10, top: 10, zIndex: 2 }}
                variant="default"
              >
                <IconFocus2 size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <PfMap
              interactiveLayerIds={[ASSIGNMENT_POINTS_LAYER_ID]}
              onClick={handleMapClickWithDrawGuard}
              onLoad={handleMapLoad}
              ref={mapRef}
            >
              <NavigationControl showCompass={false} />
              <DrawControl
                controls={{ polygon: true, trash: true }}
                limit={1}
                onCreate={handleDrawCreate}
                onDelete={handleDrawDelete}
                onUpdate={handleDrawUpdate}
                onLoad={(draw) => {
                  drawRef.current = draw;
                }}
                position="top-right"
              />
              <Source
                data={pointsGeoJson}
                id={ASSIGNMENT_POINTS_SOURCE_ID}
                promoteId="id"
                type="geojson"
              >
                <Layer
                  id={ASSIGNMENT_POINTS_LAYER_ID}
                  paint={{
                    'circle-radius': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      5,
                      2,
                      8,
                      4,
                      11,
                      6,
                      14,
                      8,
                    ],
                    'circle-color': ['get', 'fillColor'],
                    'circle-opacity': ['get', 'fillOpacity'],
                    'circle-stroke-width': ['get', 'strokeWidth'],
                    'circle-stroke-color': ['get', 'strokeColor'],
                  }}
                  type="circle"
                />
              </Source>
              {isNgp && !isTeamAssignment && showDepartmentShares ? (
                <DepartmentShareOverlay shares={mapQuery.data?.department_shares ?? []} />
              ) : null}
              <AssignmentPointPopup
                canForceOrganic={isNgp && !isTeamAssignment}
                data={popupData}
                forceOrganicLoading={forceOrganicMutation.isPending}
                i18n={i18n}
                onClose={() => setPopupData(null)}
                onSetOrganicForced={handleSetOrganicForced}
                showContractLink={!isTeamAssignment}
                showStoniness={isNgp}
              />
              <AssignmentMapLegendPanel
                colorMode={colorMode}
                i18n={i18n}
                isNgp={isNgp}
                legendPeriod={legendPeriod}
                onLegendPeriodChange={setLegendPeriod}
                points={filteredMapPoints}
                samplerLegendMode={capabilities.samplerLegendMode}
                variant="overlay"
              />
            </PfMap>
          </Paper>
        </Box>
      </FullMainHeight.FlexContent>
      {isNgp && !isTeamAssignment ? (
        <NgpSamplingRecapModal
          onClose={() => setRecapOpened(false)}
          opened={recapOpened}
        />
      ) : null}
    </FullMainHeight.Root>
  );
};

